/**
 * @file SelectAssessmentTags/index.js
 * @module SelectAssessmentTags
 * @desc This module exports the SelectAssessmentTags component and provides utility functions for working with assessment tags.
 */
import selectedAssessmentTag from "./SelectAssessmentTags";
import AssessmentConfigurationTags from "./AssessmentConfigurationTags.json";

/**
 * Finds the selected assessment tag string from the saved tags.
 * @param {Array} configurationTags - The array of configuration tags (contentTags / licensedContent)
 * @returns {string|null} - The selected assessment tag string or null if not found.
 */
export const findSelectedAssessmentTagFromSavedTags = (configurationTags) => {
  if (!configurationTags) return null;
  const tag = configurationTags.find(tag => tag.startsWith("ASSESSMENT-") && tag.endsWith("_RAN"))
  // remove the _RAN suffix and then remove anything after the last underscore
  return tag ? tag.replace("_RAN", "").replace(/_[^_]*$/, "") : null;
};

/**
 * Finds the selected assessment tag object from the saved tags
 * @param {Array} configurationTags 
 * @returns {Object|null} - The selected assessment tag object or null if not found.
 */
export const findSelectedAssessmentTagObjectFromSavedTags = (configurationTags) => {
  if (!configurationTags) return null;
  const tag = findSelectedAssessmentTagFromSavedTags(configurationTags);
  return AssessmentConfigurationTags.find(tagConfig => tagConfig.value === tag);
};

/**
 * Finds the selected RAN tag from the saved tags.
 * @param {Array} configurationTags - The array of configuration tags (contentTags / licensedContent)
 * @returns {string|null} - The selected RAN tag or null if not found.
 */
export const findSelectedRanTagFromSavedTags = (configurationTags) => {
  if (!configurationTags) return null;
  const tag = configurationTags.find(tag => tag.startsWith("ASSESSMENT-") && tag.endsWith("_RAN"))
  const assessmentTag = findSelectedAssessmentTagFromSavedTags(configurationTags);
  // remove the assessment tag and the underscore
  return tag ? tag.replace(`${assessmentTag}_`, "") : null;
}

/**
 * Creates combined tags from the selected assessment tag.
 * It uses the RAN override if it exists.
 * @param {string} selectedAssessmentTag - The selected assessment tag.
 * @param {string} selectedRANTag - The selected RAN tag.
 * @returns {string} - The combined tags.
 */
export const createCombinedTagsFromSelectedAssessmentTag = (selectedAssessmentTag, selectedRANTag) => {
  return `${selectedAssessmentTag}_${selectedRANTag}`;
};

/**
 * Checks if the selected assessment tag has a RAN override.
 * @param {string} selectedAssessmentTag - The selected assessment tag.
 * @returns {boolean} - True if the selected assessment tag has a RAN override, false otherwise.
 */
export const hasRanOverride = (selectedAssessmentTag) => {
  return !!getRanOverride(selectedAssessmentTag);
}

/**
 * Gets the RAN override for the selected assessment tag.
 * @param {string} selectedAssessmentTag - The selected assessment tag.
 * @returns {string|null} - The RAN override or null if it does not exist.
 */
export const getRanOverride = (selectedAssessmentTag) => {
  const selectedAssessmentTagConfig = AssessmentConfigurationTags.find(tag => tag.value === selectedAssessmentTag);
  return selectedAssessmentTagConfig?.ran_override;
}

/**
 * Combines the content tags with the assessment and RAN tags
 * @param {Array} contentTags - The content tags.
 * @param {string} combinedAssessmentAndRANTags - The combined assessment and RAN tags.
 * @returns {Array} - The combined content tags.
 */
export const combinedContentTags = (contentTags, combinedAssessmentAndRANTags) => {

  if(!contentTags){
    return [combinedAssessmentAndRANTags];
  }

  const hasAssessmentTag = contentTags.some(tag => tag.startsWith("ASSESSMENT-"));
  if(!hasAssessmentTag) return [...contentTags, combinedAssessmentAndRANTags];

  return contentTags.map(tag => {
    if (tag.startsWith("ASSESSMENT-")) {
      return combinedAssessmentAndRANTags;
    }
    return tag;
  }
  );
}

export default selectedAssessmentTag;